import { Flex } from '@smarterservices/smarter-ui';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SinglePane from '../SinglePane';
import PaneViewer from '../PaneViewer';
import StatusItem from '../Common/StatusItem';
import { sendMessageToExtension } from '@session/helpers/helpers';
import { provisionExam } from '@session/components/ExamLockdownAndStart/api';

const ExamLockdownAndStart = () => {
  const [searchParams] = useSearchParams();
  const [lockingDown, setLockingDown] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  const sessionSid = searchParams.get('sessionSid');
  const examSid = searchParams.get('examSid');
  const installSid = searchParams.get('installSid');
  const token = searchParams.get('token');

  useEffect(() => {
    // Store params in sessionStorage for API calls
    const lockdownAndProvision = async () => {
      // Helper function to ensure minimum display time
      const ensureMinimumDelay = async (startTime) => {
        const minDelay = 2000 + Math.random() * 1000; // Random delay between 2-3 seconds
        const elapsed = Date.now() - startTime;
        if (elapsed < minDelay) {
          await new Promise(resolve => setTimeout(resolve, minDelay - elapsed));
        }
      };

      // Lockdown step with retry logic
      const lockdownStart = Date.now();
      let lockdownSuccess = false;
      const maxRetries = 5;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const response = await sendMessageToExtension({
          message: 'sp_background__enableDisabledModalityOptions',
        });

        if (response) {
          lockdownSuccess = true;
          break;
        }

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (!lockdownSuccess) {
        sendMessageToExtension({
          message: 'sp_background__endSession',
          disconnectReason: 'test-taker-ended',
          popup: {
            body: 'Your session was ended',
            reason: 'The exam could not be locked down'
          }
        });
        return;
      }

      setLockingDown(true);
      await ensureMinimumDelay(lockdownStart);

      // Provisioning step
      try {
        const provisionStart = Date.now();
        const params = {
          sessionSid,
          examSid,
          installSid,
          token
        };
        const result = await provisionExam(params);
        setProvisioning(true);
        await ensureMinimumDelay(provisionStart);

        const stopPayload = {
          message: 'sp_background__stopExamLockdownAndStart'
        }

        if (result.redirectUrl) {
          stopPayload.redirectUrl = result.redirectUrl;
        }

        await sendMessageToExtension(stopPayload);
      } catch (error) {
        // end the session because the exam could not be provisioned
        sendMessageToExtension({
          message: 'sp_background__endSession',
          disconnectReason: 'test-taker-ended',
          popup: {
            body: 'Your session was ended',
            reason: 'The exam could not be provisioned'
          }
        });
      }
    }
    if (sessionSid && examSid && installSid && token) {
      lockdownAndProvision();
    } else {
      sendMessageToExtension({
        message: 'sp_background__stopExamLockdownAndStart',
      });
    }
  }, [sessionSid, examSid, installSid, token]);

  const step = {
    stepData: {
      title: 'Exam Lockdown and Start'
    }
  };

  return (
    <PaneViewer
      step={step}
      nextEnabled={false}
      onNext={null}
      inSession={true}
    >
      <SinglePane>
        <Flex
          as="div"
          direction="column"
          height="100%"
          justifyContent="center"
          alignItems="center"
          padding="medium"
        >
          <Flex as="div" direction="column" alignItems="start">
            <StatusItem label="Locking Down" completed={lockingDown} />
            <StatusItem label="Provisioning" completed={provisioning} />
          </Flex>
        </Flex>
      </SinglePane>
    </PaneViewer>
  );
};

export default ExamLockdownAndStart;
